/**
 * §P.5 · Un cinturón, pintado.
 *
 * ## Por qué es un componente y no un `<span>` con estilo en línea
 *
 * Aparece en cinco sitios —la escalera del club, el pasaporte del alumno, la lista
 * de candidatos, el examen y la ficha— y **el color no puede salir de una variable
 * CSS**: las de marca están prohibidas aquí (`BFF_BO.md` §9, el verde del cinturón
 * verde es verde en todos los clubes) y el color real viene del servidor por fila.
 * Con estilo en línea repetido cinco veces, el bicolor se pinta bien en tres sitios
 * y mal en dos.
 *
 * ## El bicolor
 *
 * Un cinturón blanco-amarillo es **mitad y mitad**, no un degradado: es cómo es la
 * prenda. Se hace con un `linear-gradient` de parada dura al 50 %, que es un
 * degradado usado para no degradar nada.
 *
 * ## Y no es solo color
 *
 * El nombre va siempre al lado. Un punto de color sin texto deja fuera a quien no
 * distingue el verde del marrón —y en un sistema de grados esos dos están a cuatro
 * peldaños de distancia—, y además hay cinturones que solo se diferencian por el
 * nombre: «negro 1.er Dan» y «negro 2.º Dan» son el mismo color.
 */
import type { ColorDeCinturon } from '@/club/grados/tipos';

export function Cinturon({
  color,
  color2,
  nombre,
  franjas,
  deDan,
}: {
  color: ColorDeCinturon;
  color2?: ColorDeCinturon | null | undefined;
  nombre: string;
  /** Las franjas puestas hoy. Se pintan sobre el cinturón, como en la prenda. */
  franjas?: number | undefined;
  deDan?: boolean | undefined;
}) {
  const fondo = color2
    ? `linear-gradient(90deg, ${color} 0 50%, ${color2} 50% 100%)`
    : color;

  return (
    <span className="cinturon">
      <span
        className="cinturon-muestra"
        style={{ background: fondo }}
        // El color ya se dice con el nombre de al lado; repetirlo aquí haría que
        // un lector de pantalla leyera «verde verde».
        aria-hidden="true"
      >
        {franjas !== undefined && franjas > 0 && (
          <span className="cinturon-franjas">
            {Array.from({ length: franjas }, (_, i) => (
              <span key={i} className="cinturon-franja" />
            ))}
          </span>
        )}
      </span>

      <span>
        {nombre}
        {franjas !== undefined && franjas > 0 && (
          <span className="tenue">
            {' '}
            · {franjas} {franjas === 1 ? 'franja' : 'franjas'}
          </span>
        )}
        {deDan && <span className="tenue"> · Dan</span>}
      </span>
    </span>
  );
}
